import { CONFIG } from '../config.js';
import { GameLoop } from './GameLoop.js';
import { InputManager } from './InputManager.js';
import { CameraSystem } from '../rendering/Camera.js';
import { RendererSystem } from '../rendering/Renderer.js';
import { GraphicsSettings } from '../rendering/GraphicsSettings.js';
import { PostProcessingPipeline } from '../rendering/postprocessing/PostProcessingPipeline.js';
import { World } from '../world/World.js';
import { Player } from '../player/Player.js';
import { NPCManager } from '../npc/NPCManager.js';
import { disposeSharedBinaryAtlas } from '../rendering/binary/sharedBinaryResources.js';
import { DigitalRain } from '../rendering/DigitalRain.js';
import { GameUI } from '../ui/GameUI.js';
import { PerformanceMonitor } from '../debug/PerformanceMonitor.js';
import { PerformanceHUD } from '../debug/PerformanceHUD.js';
import { countFrustumVisibleRegions } from '../debug/SceneMetrics.js';
import { AudioManager } from '../audio/AudioManager.js';
import { WorldEventDirector } from '../world/events/WorldEventDirector.js';
import { isPerfHudEnabled } from '../debug/debugLog.js';
import { isPositionInRoadPedestrianCorridor } from '../world/streaming/worldGrid.js';
import * as THREE from 'three';

/**
 * Top-level orchestrator. Owns subsystem instances; no global singleton.
 */
export class Game {
  /** @param {{ canvas: HTMLCanvasElement }} options */
  constructor({ canvas }) {
    this._canvas = canvas;
    this._disposed = false;

    this.input = new InputManager();
    this.graphicsSettings = new GraphicsSettings();
    this.renderer = new RendererSystem({ canvas, config: CONFIG.renderer });
    this.camera = new CameraSystem({
      aspect: this._aspect(),
      config: CONFIG.camera,
    });
    this.postProcessing = new PostProcessingPipeline({
      renderer: this.renderer.threeRenderer,
      scene: this.renderer.scene,
      camera: this.camera.threeCamera,
      settings: this.graphicsSettings,
    });
    this.renderer.setPostProcessingPipeline(this.postProcessing);
    this.world = new World({ config: CONFIG.world });
    this.player = new Player({
      canvas,
      input: this.input,
      camera: this.camera,
      world: this.world,
    });
    this.npcs = new NPCManager({ world: this.world });
    this.rain = new DigitalRain({ graphicsSettings: this.graphicsSettings });
    this.audio = new AudioManager();
    this.ui = new GameUI({
      hudRootSelector: CONFIG.hud.rootSelector,
      menuRootSelector: CONFIG.ui.menuRootSelector,
      graphicsSettings: this.graphicsSettings,
      onEnterGame: () => this._onEnterSimulation(),
      onResumeGame: () => this._onResumeSimulation(),
      onSettingsApplied: () => this._applyUiSettings(),
      onUiInteract: () => this._onUiInteract(),
    });
    this.worldEvents = new WorldEventDirector({
      config: CONFIG.events,
      getDeps: () => ({
        chunkMaterials: this.world.getChunkManager(),
        atmosphere: this.world.getAtmosphereEffects(),
        flyingShips: this.world.getFlyingShipFleet(),
        roadVehicles: this.world.getRoadVehicleTraffic(),
        npcs: this.npcs,
        rain: this.rain,
        glitch: this.postProcessing.glitchController,
        ui: this.ui,
        audio: this.audio,
      }),
    });
    this.perfMonitor = new PerformanceMonitor();
    this.perfHud = new PerformanceHUD();

    this.player.setLockChangeHandler((locked) => {
      this.ui.setPointerLocked(locked);
    });
    this.player.addFootstepListener((event) => {
      if (!CONFIG.audio.enabled || !this.audio.started) return;
      this.audio.footsteps?.onStep(event);
    });
    this.postProcessing.glitchController.onTrigger = () => {
      if (!CONFIG.audio.enabled || !this.audio.started) return;
      if (!this.graphicsSettings.glitch) return;
      this.audio.ui?.playGlitch();
    };

    this.loop = new GameLoop({
      onUpdate: (delta) => this._update(delta),
      onRender: () => this._render(),
      maxDeltaSeconds: CONFIG.loop.maxDeltaSeconds,
    });

    this._onResize = () => this._handleResize();
    this._npcPoolCount = CONFIG.npc.count;
    this._interiorRainWasEnabled = true;
    this._interiorGroundProbe = new THREE.Vector3();
  }

  start() {
    if (this._disposed) return;

    this.world.attachToScene(this.renderer.scene);
    this.ui.mount();
    this._applyUiSettings();

    this.renderer.scene.add(this.rain.group);
    this.player.initializeSpawn(this.world.getSpawnPoint());

    const poolMax = CONFIG.ui.npcPoolMax;
    this._npcPoolCount = CONFIG.npc.count;
    CONFIG.npc.count = poolMax;
    this.npcs.initialize();
    CONFIG.npc.count = this._npcPoolCount;
    this.npcs.setActiveCount(this.ui.settings.npcDensity);
    this.npcs.setFootstepHandler((npc, event) => this._onNpcFootstep(npc, event));

    this.world.update(0, this.player.position);
    this.renderer.scene.add(this.npcs.group);
    this.perfMonitor.attachRenderer(this.renderer.threeRenderer);
    if (isPerfHudEnabled()) {
      this.perfHud.mount(document.querySelector(CONFIG.hud.rootSelector));
    }
    this.player.setInteractionEnabled(false);

    this._handleResize();
    window.addEventListener('resize', this._onResize);
    this.input.attach(window);
    this.loop.start();
  }

  _onEnterSimulation() {
    void this.audio.ensureStarted();
    this.player.setInteractionEnabled(true);
  }

  _onResumeSimulation() {
    if (!this.worldEvents?.isGlitchEventActive) {
      this.npcs.setWorldFrozen(false);
    }
    this.player.setInteractionEnabled(true);
  }

  /** @param {import('../npc/NPC.js').NPC} npc @param {import('../player/FootstepController.js').FootstepEvent} event */
  _onNpcFootstep(npc, event) {
    if (!CONFIG.audio.enabled || !this.audio.started) return;
    if (!this.ui.sessionActive || this.ui.isMenuOpen) return;

    const player = this.player.position;
    const dx = npc.position.x - player.x;
    const dz = npc.position.z - player.z;
    const distance = Math.hypot(dx, dz);
    const maxDist = CONFIG.audio.npcFootsteps.maxDistance;
    if (distance > maxDist) return;

    const pan = Math.sin(Math.atan2(dx, dz) - this.player.look.yaw);
    const onRoad = isPositionInRoadPedestrianCorridor(
      npc.position.x,
      npc.position.z,
      CONFIG.city,
    );
    this.audio.footsteps?.onNpcStep(event, { distance, pan, onRoad });
  }

  _onUiInteract() {
    if (!CONFIG.audio.enabled) return;
    void this.audio.ensureStarted();
    this.audio.ui?.playClick();
  }

  _updateInteriors(delta) {
    const interiors = this.world.getInteriorManager();
    const registry = this.world.getEntranceRegistry();
    const inPlay =
      this.ui.sessionActive &&
      !this.ui.isMenuOpen &&
      this.player.look.isLocked;

    if (!CONFIG.interiors?.enabled || !registry || !inPlay) {
      this.ui.setInteractHint(null);
      return;
    }

    interiors.update(delta, {
      player: this.player,
      registry,
      consumeInteract: () =>
        this.input.consumeKey(CONFIG.interiors.interactKey),
      onEnter: () => this._onInteriorEnter(),
      onExit: () => this._onInteriorExit(),
      onTransition: (opacity) => this.ui.setScreenFade(opacity),
      findExteriorGround: (x, z) => {
        this._interiorGroundProbe.set(x, 0, z);
        return this.player.collision.findGroundHeight(
          this._interiorGroundProbe,
          this.world.getExteriorColliders(this._interiorGroundProbe),
        );
      },
    });

    const prompt = interiors.getInteractionPrompt(registry, this.player.position);
    this.ui.setInteractHint(prompt);
  }

  _onInteriorEnter() {
    this._interiorRainWasEnabled = this.graphicsSettings.rainEnabled;
    this.rain.setVisible(false);
    this.npcs.setWorldFrozen(true);
    const label =
      this.world.getInteriorManager().activeDesignName ?? 'INTERIOR';
    this.ui.flashWorldEvent(`${label.toUpperCase()}`, 1.6);
    if (CONFIG.audio.enabled && this.audio.started) {
      this.audio.ui?.playClick();
    }
  }

  _onInteriorExit() {
    if (this._interiorRainWasEnabled) {
      this.rain.setVisible(this.graphicsSettings.rainEnabled);
    }
    if (!this.worldEvents?.isGlitchEventActive) {
      this.npcs.setWorldFrozen(false);
    }
    this.ui.flashWorldEvent('RETURNED TO GRID', 1.2);
    if (CONFIG.audio.enabled && this.audio.started) {
      this.audio.ui?.playClick();
    }
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;

    this.loop.stop();
    window.removeEventListener('resize', this._onResize);
    this.input.detach();
    this.npcs.dispose();
    this.rain.dispose();
    this.player.dispose();
    this.world.dispose();
    this.worldEvents = null;
    this.ui.dispose();
    this.perfHud.dispose();
    this.audio.dispose();
    disposeSharedBinaryAtlas();
    this.renderer.dispose();
  }

  _update(delta) {
    this.perfMonitor.tick(delta);
    this.input.update();

    if (this.ui.sessionActive && !this.ui.isMenuOpen) {
      if (this.input.consumeKey('Escape')) {
        this.player.look.exitLock();
        this.player.setInteractionEnabled(false);
        this.npcs.setWorldFrozen(true);
        this.ui.openMenu();
      }
    }

    this._updateInteriors(delta);

    this.player.update(delta);
    this.world.update(delta, this.player.position);
    if (!this.world.getInteriorManager().isInside) {
      this.npcs.update(delta, this.player.position);
    }
    if (CONFIG.events?.enabled) {
      this.worldEvents.update(delta, {
        inSimulation: this.ui.sessionActive && !this.ui.isMenuOpen,
      });
    }
    this.rain.update(delta, this.camera.threeCamera.position);
    this.postProcessing.update(delta);
    this.ui.setNpcCount(this.npcs.activeCount);
    this.ui.updateTelemetry(this.player.position, this.perfMonitor.fps);
    this.ui.hud.update(delta);

    if (CONFIG.audio.enabled) {
      const horizontalSpeed = Math.hypot(
        this.player.velocity.x,
        this.player.velocity.z,
      );
      const inSimulation = this.ui.sessionActive && !this.ui.isMenuOpen;
      const pos = this.player.position;
      this.audio.update(delta, {
        horizontalSpeed,
        sprinting:
          this.input.isKeyDown('ShiftLeft') ||
          this.input.isKeyDown('ShiftRight'),
        onGround: this.player.onGround,
        inSimulation,
        menuOpen: this.ui.isMenuOpen,
        nearbyNpcCount: this.npcs.countWithinRadius(
          pos,
          CONFIG.audio.crowd.hearRadius,
        ),
        shipAudio: this.world
          .getFlyingShipFleet()
          .getAudioSnapshot(pos, this.player.look.yaw),
        rainActive: this.rain.isAudible,
        rainIntensity: this.rain.intensityMultiplier,
        vehicleAudio: this.world
          .getRoadVehicleTraffic()
          .getAudioSnapshot(pos, this.player.look.yaw),
      });
    }
  }

  _render() {
    this.renderer.render(this.camera.threeCamera);
    this.perfMonitor.sampleRenderInfo();

    if (isPerfHudEnabled()) {
      const stream = this.world.getStreamingStats();
      const buildingsVisible = countFrustumVisibleRegions(
        this.camera.threeCamera,
        stream.chunkBounds,
      );
      this.perfHud.update({
        fps: this.perfMonitor.fps,
        frameMs: this.perfMonitor.frameMs,
        drawCalls: this.perfMonitor.drawCalls,
        triangles: this.perfMonitor.triangles,
        geometries: this.perfMonitor.geometries,
        textures: this.perfMonitor.textures,
        npcCount: this.npcs.activeCount,
        npcFull: this.npcs.lodStats.full,
        npcMedium: this.npcs.lodStats.medium,
        npcFar: this.npcs.lodStats.far,
        npcStatic: this.npcs.lodStats.static,
        activeChunks: stream.activeChunks,
        buildingsLoaded: stream.buildingsLoaded,
        buildingsVisible,
      });
    }
  }

  _handleResize() {
    const { clientWidth, clientHeight } = this._canvas;
    this.renderer.setSize(clientWidth, clientHeight);
    const cap = this.graphicsSettings.pixelRatioMax ?? CONFIG.renderer.pixelRatioMax;
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, cap),
    );
    this.camera.setAspect(this._aspect());
  }

  _aspect() {
    const w = this._canvas.clientWidth || 1;
    const h = this._canvas.clientHeight || 1;
    return w / h;
  }

  _applyUiSettings() {
    this.ui.settings.syncGraphicsSettings();
    this.graphicsSettings.setPostProcessingEnabled(
      this.ui.settings.postProcessing,
    );
    if (this.ui.settings.bloom && this.ui.settings.quality !== 'low') {
      this.graphicsSettings.bloom = true;
    }

    const renderDistance =
      this.graphicsSettings.renderDistance ??
      CONFIG.streaming?.renderDistance ??
      CONFIG.graphics.renderDistance;
    this.graphicsSettings.renderDistance = renderDistance;
    this.camera.threeCamera.far = Math.max(
      renderDistance,
      CONFIG.camera.far,
    );
    this.camera.threeCamera.updateProjectionMatrix();
    this.world.applyGraphicsSettings(this.graphicsSettings);
    this.rain.setVisible(this.graphicsSettings.rainEnabled);

    if (this.npcs.activeCount !== undefined) {
      this.npcs.setActiveCount(this.ui.settings.npcDensity);
    }

    const cap = this.ui.settings.getPixelRatioCap();
    this.graphicsSettings.pixelRatioMax = cap;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));
    const { clientWidth, clientHeight } = this._canvas;
    this.renderer.setSize(clientWidth, clientHeight);
  }
}
